<template>
  <SettingsDialog v-model="active">
    <div class="d-flex h-100 w-100">
      <SettingsDialogListGroup
        v-if="!isMobile || !active"
        v-model="active"
        :list="settingsList"
        :class="{'w-100': isMobile}"
      >
        <BootstrapButton
          id="settings.log-out"
          name="Log out"
          class="mt-auto text-danger"
          variant="ghost"
          :size="isMobile ? 'lg' : 'sm'"
          @click="logout"
        >
          <Icon icon="material-symbols:logout" />
          {{ translate('Log out') }}
        </BootstrapButton>
      </SettingsDialogListGroup>
      <section
        v-if="!isMobile || active"
        class="w-100 flex-grow-1 overflow-auto d-flex flex-column"
        :class="isMobile ? 'px-3 py-3' : 'px-5 py-4'"
      >
        <div class="d-flex align-items-center mb-3">
          <BootstrapButton
            v-if="isMobile"
            variant="ghost"
            :size="isMobile ? 'lg' : 'sm'"
            :aria-label="translate('Back')"
            class="py-1 ps-0 pe-2"
            @click="active = ''"
          >
            <Icon icon="material-symbols:arrow-back" />
          </BootstrapButton>
          <h5 class="mb-0">{{ translate(active) }}</h5>
        </div>

        <KeepAlive>
          <component :is="getComponentName(active)" :loading="loading" class="flex-grow-1" />
        </KeepAlive>
      </section>
    </div>
  </SettingsDialog>
</template>
<script lang="ts" setup>
import {useLocalisation} from '@/composables/useLocalisation';
import PreferencesView from '@/components/settings/PreferencesView/PreferencesView.vue';
import RolesView from '@/components/settings/RolesView.vue';
import CustomerProductsView from '@/components/settings/CustomerProductsView.vue';
import ProfileView from '@/components/settings/ProfileView.vue';
import LoginPolicyApp from '@/components/loginPolicy/LoginPolicyApp.vue';

import {logoutKey} from '@/App.vue';
import {settingsKey, SettingsKey} from '@/components/settings/injectionKeys';

const logout = inject(logoutKey);
const active = inject(settingsKey)!;

const userStore = useUserStore();
const {translate} = useLocalisation();
const {isMobile} = useBsBreakpoints();
const loading = ref(false); // TODO: Update/merge settings folder.

// TODO: Make all these async components??
const componentMap = new Map<SettingsKey, Component>([
  ['Preferences', PreferencesView],
  ['Users', defineAsyncComponent(() => import('@/components/settings/UsersView/UsersView.vue'))],
  ['Profile', ProfileView],
  ['Roles', RolesView],
  ['Products', CustomerProductsView],
  [
    'Ekco Users',
    defineAsyncComponent(() => import('@/components/settings/EkcoUsersView/EkcoUsersView.vue')),
  ],
  ['Login Policy', LoginPolicyApp],
  [
    'Contacts',
    defineAsyncComponent(() => import('@/components/settings/ContactsView/ContactsView.vue')),
  ],
  [
    'Sources',
    defineAsyncComponent(() => import('@/components/settings/SourcesView/SourcesView.vue')),
  ],
  [
    'Demo Customers',
    defineAsyncComponent(
      () => import('@/components/settings/DemoCustomerView/DemoCustomerView.vue'),
    ),
  ],
  [
    'Login Policy',
    defineAsyncComponent(() => import('@/components/loginPolicy/LoginPolicyApp.vue')),
  ],
]);
const getComponentName = (name: SettingsKey) => componentMap.get(name);

const settingsList = computed<Map<string | undefined, [SettingsKey, string][]>>(() => {
  // For legibility's sake.
  const loginPolicy = userStore.hasAnyPermission(['login-policy:read', 'login-policy:read']);

  const accountItems: [SettingsKey, string][] = [
    ['Profile', 'account-circle'],
    ['Preferences', 'settings-outline'],
  ];
  const userManagementItems: [SettingsKey, string][] = [];
  const customerItems: [SettingsKey, string][] = [];

  if (userStore.isUserManager) {
    userManagementItems.push(['Users', 'person-outline']);
    userManagementItems.push(['Roles', 'verified-user']);
  }
  if (userStore.isEkcoUserManager) {
    userManagementItems.push(['Ekco Users', 'group-outline']);
  }

  customerItems.push(['Contacts', 'contacts-outline']);
  if (userStore.isUserManager || userStore.isCustomerProductManager)
    customerItems.push(['Products', 'order-approve']);
  if (userStore.isCustomerMappingManager) customerItems.push(['Sources', 'graph-2']);
  if (loginPolicy) customerItems.push(['Login Policy', 'domain-verification-outline']);
  if (userStore.isEkcoUserManager) customerItems.push(['Demo Customers', 'storefront-outline']);

  const entries: [string | undefined, [SettingsKey, string][]][] = [
    ['Account', accountItems],
    ['User Management', userManagementItems],
    ['Customer', customerItems],
  ];
  return new Map<string | undefined, [SettingsKey, string][]>(entries);
});
</script>
<style lang="scss" scoped>
.settings-list-group {
  margin-right: var(--bs-card-spacer-x);
}
</style>
